import React, { useState, useEffect } from 'react';
import { Award, Users, Star } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Marchi = () => {
  const [brandsRappresentanza, setBrandsRappresentanza] = useState([]);
  const [brandsDistribuzione, setBrandsDistribuzione] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axios.get(`${API}/brands`);
        const brands = response.data;
        
        // Separa i marchi per tipo
        setBrandsRappresentanza(brands.filter(b => b.brand_type === 'Rappresentanza'));
        setBrandsDistribuzione(brands.filter(b => b.brand_type === 'Distribuzione'));
        setLoading(false);
      } catch (error) {
        console.error('Error fetching brands:', error);
        setLoading(false);
      }
    };

    fetchBrands();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Caricamento marchi...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center overflow-hidden bg-gradient-to-br from-slate-800 to-slate-700">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-orange-600 text-white mb-6">
              <Award size={40} />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Marchi Trattati
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Rivenditore dei migliori marchi per l'edilizia e l'agricoltura
            </p>
          </div>
        </div>
      </section>

      {/* Brands Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          {/* Rappresentanza Section */}
          <div className="mb-20">
            <div className="text-center max-w-4xl mx-auto mb-12">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-blue-600 text-white mb-6">
                <Star size={40} />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
                Marchi in Rappresentanza Esclusiva
              </h2>
              <p className="text-xl text-slate-600 leading-relaxed">
                Collaboriamo direttamente con questi brand offrendo <strong>assistenza tecnica specializzata</strong> grazie all'esperienza di <strong className="text-blue-600">Francesco Valerio</strong>, tecnico con oltre <strong className="text-blue-600">30 anni di esperienza</strong> nel settore edilizio.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              {brandsRappresentanza.length === 0 ? (
                <div className="col-span-full text-center py-8 text-slate-600">
                  Nessun marchio in rappresentanza disponibile
                </div>
              ) : (
                brandsRappresentanza.map((brand) => (
                  <a 
                    key={brand.brand_id}
                    href={brand.website || '#'}
                    target={brand.website ? "_blank" : "_self"}
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <Card 
                      className="group hover:shadow-2xl transition-all duration-300 border-2 border-blue-200 hover:border-blue-500 overflow-hidden bg-gradient-to-br from-blue-50 to-white cursor-pointer"
                    >
                      <CardContent className="p-8 flex flex-col items-center justify-center min-h-[200px]">
                        {brand.logo_url && (
                          <div className="w-full h-24 flex items-center justify-center mb-6 px-4">
                            <img 
                              src={brand.logo_url} 
                              alt={`${brand.name} logo`}
                              className="max-w-full max-h-full object-contain group-hover:scale-110 transition-transform"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `https://via.placeholder.com/300x120/dbeafe/1e40af?text=${encodeURIComponent(brand.name)}`;
                              }}
                            />
                          </div>
                        )}
                        <h3 className="text-xl font-bold text-blue-700 group-hover:text-blue-600 transition-colors text-center mb-2" translate="no">
                          {brand.name}
                        </h3>
                        <span className="text-xs bg-blue-600 text-white px-3 py-1 rounded-full font-semibold">
                          RAPPRESENTANZA
                        </span>
                      </CardContent>
                    </Card>
                  </a>
                ))
              )}
            </div>

            <div className="mt-12 p-8 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl text-white text-center max-w-4xl mx-auto">
              <Users size={48} className="mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-3">Supporto Tecnico Dedicato</h3>
              <p className="text-blue-100 text-lg leading-relaxed">
                Per questi marchi offriamo consulenza tecnica specializzata, formazione e supporto diretto dal nostro team di esperti. Francesco Valerio è disponibile per sopralluoghi, progetti personalizzati e assistenza completa.
              </p>
            </div>
          </div>

          {/* Distribuzione Section */}
          <div>
            <div className="text-center max-w-4xl mx-auto mb-12">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-slate-700 text-white mb-6">
                <Award size={40} />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
                Marchi in Distribuzione Autorizzata
              </h2>
              <p className="text-xl text-slate-600">
                Partner ufficiali per la distribuzione di prodotti certificati e di alta qualità
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 max-w-7xl mx-auto">
              {brandsDistribuzione.length === 0 ? (
                <div className="col-span-full text-center py-8 text-slate-600">
                  Nessun marchio in distribuzione disponibile
                </div>
              ) : (
                brandsDistribuzione.map((brand) => (
                  <a 
                    key={brand.brand_id}
                    href={brand.website || '#'}
                    target={brand.website ? "_blank" : "_self"}
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <Card 
                      className="group hover:shadow-2xl transition-all duration-300 border-2 border-slate-200 hover:border-slate-400 overflow-hidden cursor-pointer"
                    >
                      <CardContent className="p-6 flex flex-col items-center justify-center min-h-[180px]">
                        {brand.logo_url && (
                          <div className="w-full h-20 flex items-center justify-center mb-4 px-4">
                            <img 
                              src={brand.logo_url} 
                              alt={`${brand.name} logo`}
                              className="max-w-full max-h-full object-contain group-hover:scale-110 transition-transform"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `https://via.placeholder.com/300x120/f1f5f9/334155?text=${encodeURIComponent(brand.name)}`;
                              }}
                            />
                          </div>
                        )}
                        <h3 className="text-lg font-bold text-slate-800 group-hover:text-slate-600 transition-colors text-center" translate="no">
                          {brand.name}
                        </h3>
                      </CardContent>
                    </Card>
                  </a>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Brand Details */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-12 text-center">
              I Nostri Partner
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Sikkens */}
              <Card className="border-none shadow-lg">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-800 mb-4">Sikkens</h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    Marchio premium di vernici professionali per interni ed esterni. Qualità, durabilità e 
                    un'ampia gamma di colori per ogni esigenza.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Vernici
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Pitture
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Finiture
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* CVR */}
              <Card className="border-none shadow-lg">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-800 mb-4">CVR</h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    Soluzioni professionali per l'edilizia. Prodotti innovativi per costruzione, 
                    ristrutturazione e manutenzione.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Materiali Edili
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Finiture
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Icobit */}
              <Card className="border-none shadow-lg">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-800 mb-4">Icobit</h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    Specialisti in impermeabilizzazione bituminosa. Membrane e guaine di alta qualità 
                    per coperture e fondazioni.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Guaine
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Membrane
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Coperture
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Akifix */}
              <Card className="border-none shadow-lg">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-800 mb-4">Akifix</h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    Sistemi di fissaggio e ancoraggio professionale. Soluzioni tecniche per ogni 
                    esigenza di cantiere.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Ancoraggi
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Fissaggi
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Accessori
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Gyproc */}
              <Card className="border-none shadow-lg">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-800 mb-4">Gyproc</h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    Leader nel cartongesso e sistemi per costruzioni a secco. Soluzioni innovative per 
                    pareti, controsoffitti e isolamento termoacustico.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Cartongesso
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Rasanti
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Isolanti
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* AREA 51 */}
              <Card className="border-none shadow-lg">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-800 mb-4">AREA 51</h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    Collanti e adesivi professionali multiuso. Tecnologia avanzata per applicazioni 
                    edili di alta precisione.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Collanti
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Adesivi
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Multiuso
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Grigolin */}
              <Card className="border-none shadow-lg">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-800 mb-4">Grigolin</h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    Vernici e pitture decorative di alta qualità. Soluzioni innovative per facciate, 
                    interni e protezione del legno.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Pitture Decorative
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Facciate
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Protezione Legno
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Kimia */}
              <Card className="border-none shadow-lg">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-800 mb-4">Kimia</h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    Specialisti in impermeabilizzazione e restauro. Prodotti chimici tecnici per 
                    edilizia e risanamento strutturale.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Impermeabilizzanti
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Restauro
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Risanamento
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Knauf */}
              <Card className="border-none shadow-lg">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-800 mb-4">Knauf</h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    Leader mondiale in sistemi per cartongesso e costruzioni a secco. Centro distribuzione 
                    autorizzato Knauf per soluzioni complete.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Cartongesso
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Isolamento
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Sistemi a Secco
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Rofix */}
              <Card className="border-none shadow-lg">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-800 mb-4">Rofix</h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    Malte e intonaci professionali. Sistemi completi per costruzione, ristrutturazione 
                    e finiture di qualità superiore.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Malte
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Intonaci
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Finiture
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Mapei */}
              <Card className="border-none shadow-lg">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-800 mb-4">Mapei</h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    Leader mondiale in adesivi e prodotti chimici per l'edilizia. Soluzioni innovative 
                    per posa, impermeabilizzazione e restauro.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Adesivi
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Impermeabilizzanti
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Sigillanti
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Herbol */}
              <Card className="border-none shadow-lg">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-slate-800 mb-4">Herbol</h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    Vernici e rivestimenti professionali per facciate. Tecnologia tedesca per protezione 
                    e decorazione di superfici esterne.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Vernici Facciate
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Rivestimenti
                    </span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      Protezione
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Assurance */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-slate-800 to-slate-700 rounded-2xl p-12 text-white text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Garanzia di Qualità Certificata
              </h2>
              <p className="text-xl text-slate-200 leading-relaxed mb-8">
                Tutti i marchi che trattiamo sono leader riconosciuti nel settore, con prodotti certificati 
                e conformi alle normative europee. La nostra selezione garantisce solo materiali di prima 
                scelta per i tuoi progetti.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="/prodotti" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white font-semibold rounded-lg shadow-lg transition-all"
                >
                  Scopri i Prodotti
                </a>
                <a 
                  href="/contatti" 
                  className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-slate-800 font-semibold rounded-lg transition-all"
                >
                  Contattaci
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Marchi;
